import { useState, useRef, useEffect } from 'react';
import {
  Search,
  Download,
  Trash2,
  Filter,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  AlertTriangle,
  Info,
  Bug,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Dropdown } from '@/components/ui/Dropdown';
import { cn } from '@/utils/cn';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  message: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

interface LogsPanelProps {
  logs: LogEntry[];
  onClear?: () => void;
  onExport?: () => void;
  className?: string;
}

export function LogsPanel({ logs, onClear, onExport, className }: LogsPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<Set<LogLevel>>(
    new Set(['debug', 'info', 'warn', 'error'])
  );
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const filteredLogs = logs.filter((log) => {
    if (!selectedLevels.has(log.level)) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.message.toLowerCase().includes(query) ||
        log.source?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const toggleLevel = (level: LogLevel) => {
    setSelectedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const levelCounts = logs.reduce(
    (acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    },
    {} as Record<LogLevel, number>
  );

  return (
    <div className={cn('flex flex-col h-full bg-white dark:bg-dark-800', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Logs</h3>
          <div className="flex items-center gap-1">
            {(['error', 'warn', 'info', 'debug'] as LogLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => toggleLevel(level)}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  selectedLevels.has(level)
                    ? getLevelButtonStyles(level)
                    : 'bg-gray-100 text-gray-400 dark:bg-dark-700'
                )}
              >
                {getLevelIcon(level)}
                <span className="capitalize">{level}</span>
                <span className="opacity-60">({levelCounts[level] || 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-md w-48 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Auto-scroll toggle */}
          <Button
            variant={autoScroll ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setAutoScroll(!autoScroll)}
            title={autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>

          {/* Clear */}
          <Button variant="ghost" size="sm" onClick={onClear} title="Clear logs">
            <Trash2 className="w-4 h-4" />
          </Button>

          {/* Export */}
          <Button variant="ghost" size="sm" onClick={onExport} title="Export logs">
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto font-mono text-sm">
        {filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            {logs.length === 0 ? 'No logs yet' : 'No logs match your filters'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-700">
            {filteredLogs.map((log) => (
              <LogEntryRow
                key={log.id}
                log={log}
                isExpanded={expandedLogs.has(log.id)}
                onToggle={() => toggleExpanded(log.id)}
              />
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
        Showing {filteredLogs.length} of {logs.length} log entries
      </div>
    </div>
  );
}

function LogEntryRow({
  log,
  isExpanded,
  onToggle,
}: {
  log: LogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

  return (
    <div
      className={cn(
        'px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700/50',
        log.level === 'error' && 'bg-red-50/50 dark:bg-red-900/10',
        log.level === 'warn' && 'bg-yellow-50/50 dark:bg-yellow-900/10'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Level Icon */}
        <span className={cn('mt-0.5', getLevelColor(log.level))}>
          {getLevelIcon(log.level)}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {formatTimestamp(log.timestamp)}
            </span>
            {log.source && (
              <span className="text-xs text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-700">
                {log.source}
              </span>
            )}
          </div>
          <p className={cn('mt-0.5 text-gray-900 dark:text-white break-words', getLevelColor(log.level))}>
            {log.message}
          </p>

          {/* Metadata */}
          {hasMetadata && (
            <>
              <button
                onClick={onToggle}
                className="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {isExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                {isExpanded ? 'Hide details' : 'Show details'}
              </button>
              {isExpanded && (
                <pre className="mt-2 p-2 text-xs bg-gray-100 dark:bg-dark-900 rounded overflow-x-auto">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getLevelIcon(level: LogLevel) {
  switch (level) {
    case 'error':
      return <AlertCircle className="w-4 h-4" />;
    case 'warn':
      return <AlertTriangle className="w-4 h-4" />;
    case 'info':
      return <Info className="w-4 h-4" />;
    case 'debug':
      return <Bug className="w-4 h-4" />;
  }
}

function getLevelColor(level: LogLevel): string {
  switch (level) {
    case 'error':
      return 'text-red-500';
    case 'warn':
      return 'text-yellow-500';
    case 'info':
      return 'text-blue-500';
    case 'debug':
      return 'text-gray-500';
  }
}

function getLevelButtonStyles(level: LogLevel): string {
  switch (level) {
    case 'error':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'warn':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'info':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'debug':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}
